import {ChangeEvent, ReactElement, useState, useRef} from 'react'
import get from 'lodash/get'
import CloseIcon from '@material-ui/icons/Close'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {Button, Select, InputLabel, FormControl, MenuItem} from '@material-ui/core'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import CircularProgress from '@material-ui/core/CircularProgress'
import {withdrawalPaymentVendors} from '@bdswiss/common-enums'

import {FailedSubmitModal} from '../../../FailedSubmitModal'
import messages from '../../../../../../../assets/messages'
import {ModalHeader} from '../../../../../../App/PopupFactory/popups/components/shared/ModalHeader'
import {putFile} from '../../../../../../../common/utils'
import {RefundWithdrawSchemaItem} from '../../../types'
import {UploadFileArea} from './UploadFileArea'
import {useCreateDepositMethodIssueVerification, useSignUploadUrl} from './hooks'
import {useStyles} from './styles'

type CantAccessDepositMethodIssueContentProps = WithNamespaces & {
  accountId: number
  clientId: number
  refundWithdrawSchema: RefundWithdrawSchemaItem[]
  onClose: () => void
  onChooseReasonRedirect: () => void
  onDepositMethodVerificationSubmittedModalOpen: () => void
}

function CantAccessDepositMethodIssueContentBase(
  props: CantAccessDepositMethodIssueContentProps,
): ReactElement {
  const classes = useStyles()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {createDepositMethodIssueVerification} = useCreateDepositMethodIssueVerification()
  const {signUploadUrl} = useSignUploadUrl()
  const [submitting, setSubmitting] = useState(false)

  const {
    accountId,
    clientId,
    t,
    refundWithdrawSchema,
    onChooseReasonRedirect,
    onClose,
    onDepositMethodVerificationSubmittedModalOpen,
  } = props

  const [error, setError] = useState<string | null>(null)
  const [comment, setComment] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [paymentMethodOptionId, setPaymentMethodOptionId] = useState<string>('')

  const handleFailedModalClose = () => setError(null)
  const handlePaymentOptionChange = (e) => setPaymentMethodOptionId(e.target.value)

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)

  const handleUpload = (file: File) => setFile(file)

  const handleFileRemove = () => {
    setFile(null)

    if (fileInputRef.current?.value) {
      fileInputRef.current.value = ''
    }
  }

  const submitDisabled = !file || !paymentMethodOptionId || submitting

  const handleSubmit = async () => {
    if (submitDisabled) return

    try {
      setSubmitting(true)

      const paymentOption = refundWithdrawSchema.find(({id}) => id === paymentMethodOptionId)
      const {
        data: {
          signedDetails: {key, signedUrl},
        },
      } = await signUploadUrl({clientId})

      await putFile(file, signedUrl)

      const {data} = await createDepositMethodIssueVerification({
        accountId,
        clientId,
        cardNumber: paymentOption?.details,
        paymentMethodOptionId,
        fileKey: key,
        comment,
        fileName: file.name,
      })

      if (data?.createDepositMethodIssueVerification) {
        setSubmitting(false)
        onClose()
        onDepositMethodVerificationSubmittedModalOpen()
      } else {
        setError('Something went wrong!')
      }
    } catch (error: unknown) {
      if (error instanceof Error && error?.message) {
        const errorMessage = get(error, 'graphQLErrors[0].message') || error.message
        setError(errorMessage)
      }
      setError('Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <FailedSubmitModal errorMessage={error} onClose={handleFailedModalClose} />
      <ModalHeader>
        <CloseIcon className={classes.closeIcon} onClick={onClose} />
        <div className={classes.title}>
          <Trans {...messages.verifyYourDepositMethodIssue} />
        </div>
        <div className={classes.subTitle}>
          <Trans {...messages.verifyYourDepositMethodIssueSubTitle} />
        </div>
      </ModalHeader>
      <div className={classes.body}>
        <FormControl fullWidth>
          <InputLabel>
            <Trans {...messages.selectPaymentMethod} />
          </InputLabel>
          <Select value={paymentMethodOptionId} onChange={handlePaymentOptionChange}>
            {refundWithdrawSchema.map(({id, details, vendor}) => (
              <MenuItem key={id} value={id}>
                {vendor === withdrawalPaymentVendors.enovipayRefund.value ||
                vendor === withdrawalPaymentVendors.creditDebitCard.value ? (
                  <Trans {...messages.cardNumberEndingIn} values={{details}} />
                ) : (
                  details
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <UploadFileArea fileInputRef={fileInputRef} onUpload={handleUpload} />
        {file ? (
          <div className={classes.uploadedFile}>
            <div className={classes.fileName}>
              <InsertDriveFileOutlinedIcon className={classes.icon} />
              {file?.name}
            </div>
            <CloseIcon className={classes.removeFile} onClick={handleFileRemove} />
          </div>
        ) : null}
        <textarea
          className={classes.comment}
          value={comment}
          onChange={handleCommentChange}
          placeholder={t(
            messages.addDetailsAboutIssue.i18nKey,
            messages.addDetailsAboutIssue.defaults,
          )}
          rows={4}
        />
        <div className={classes.buttons}>
          <Button
            className={classes.button}
            fullWidth
            color="primary"
            variant="outlined"
            onClick={onChooseReasonRedirect}
          >
            <Trans {...messages.cancel} />
          </Button>
          <Button
            className={classes.button}
            disabled={submitDisabled}
            fullWidth
            color="primary"
            variant="contained"
            onClick={handleSubmit}
          >
            {submitting ? (
              <CircularProgress className={classes.loader} size={12} />
            ) : (
              <Trans {...messages.submit} />
            )}
          </Button>
        </div>
      </div>
    </>
  )
}

const CantAccessDepositMethodIssueContent = withNamespaces()(
  CantAccessDepositMethodIssueContentBase,
)

export {CantAccessDepositMethodIssueContent}
